/**
 * BASE PROMPT — Background file-indexing agent (user message)
 * ============================================================================
 * This is the USER-role message paired with the indexing system prompt in
 * ./indexing_system_prompt.ts. It's sent by notifyAgentSaveAttachment() in
 * ai_agent.ts each time a file is uploaded (dbfile.vue) or re-indexed. It tells
 * the agent which file to index and hands it the metadata plus a short-lived
 * signed URL to fetch the contents.
 *
 * NOTE: the leading line "A new file has just been uploaded. Index it now." is
 * also what agent.vue parses to build the "Indexing: <name>" history bubble (it
 * matches the "- name: ..." line), so keep that field on its own line.
 *
 * HOW TO TWEAK
 * ------------
 * - Edit the wording below directly. Only `name` and the temporary `url` are
 *   always present; `mime` and `size` lines are added only when known.
 * - The temporary URL expires (signed, short-lived) — the agent must fetch it
 *   promptly, which the system prompt instructs it to do.
 */

export type IndexingAttachmentInfo = {
	/** Original file name. */
	name: string;
	/** Storage path within the project's db storage. */
	storagePath: string;
	/** MIME type, if detected. Omitted from the message when unknown. */
	mime?: string;
	/** File size in bytes, if known. Omitted from the message when unknown. */
	size?: number;
	/** Temporary signed URL the agent/MCP fetches to read the file contents. */
	url: string;
};

export function buildIndexingUserMessage(attachment: IndexingAttachmentInfo): string {
	return (
		`A new file has just been uploaded. Index it now.\n\n` +
		`File metadata:\n` +
		`- name: ${attachment.name}\n` +
		`- storage path: ${attachment.storagePath}\n` +
		(attachment.mime ? `- mime type: ${attachment.mime}\n` : '') +
		(typeof attachment.size === 'number' ? `- size (bytes): ${attachment.size}\n` : '') +
		`- temporary URL (fetch this to read the file contents): ${attachment.url}`
	);
}
